import React, { createContext, useContext, useMemo, useState, useCallback } from "react";
import { reconcileOrientationEvidence } from "../components/config/orientationEvidence";
import { reconcileGridExportLimit } from "../components/config/gridExportLimit";

const HistoryContext = createContext(null);

function deepEqual(a, b) {
  // good enough for config JSON (no functions, no Dates)
  return JSON.stringify(a) === JSON.stringify(b);
}

export function HistoryProvider({ children }) {
  const [past, setPast] = useState([]);
  const [present, setPresent] = useState(null);
  const [future, setFuture] = useState([]);

  // ✅ baseline = last saved/opened state
  const [saved, setSaved] = useState(null);

  const canUndo = past.length > 0;
  const canRedo = future.length > 0;
  const clone = (x) => structuredClone(x);

  const init = useCallback((initial, savedBaseline = initial) => {
    const snap = reconcileGridExportLimit(clone(initial));
    setPast([]);
    setFuture([]);
    setPresent(snap);
    setSaved(clone(savedBaseline)); // server baseline can differ from edits made during save
  }, []);

  const reset = useCallback((initial) => {
    const snap = reconcileGridExportLimit(clone(initial));
    setPast([]);
    setFuture([]);
    setPresent(snap);
    setSaved(clone(snap));
  }, []);


  const commit = useCallback((next) => {
    setPast((p) => (present == null ? p : [...p, present]));
    setPresent(reconcileGridExportLimit(reconcileOrientationEvidence(present, next)));
    setFuture([]);
  }, [present]);

  const undo = useCallback(() => {
    setPast((p) => {
      if (p.length === 0) return p;
      const prev = p[p.length - 1];
      setFuture((f) => (present == null ? f : [present, ...f]));
      setPresent(prev);
      return p.slice(0, -1);
    });
  }, [present]);

  const redo = useCallback(() => {
    setFuture((f) => {
      if (f.length === 0) return f;
      const next = f[0];
      setPast((p) => (present == null ? p : [...p, present]));
      setPresent(next);
      return f.slice(1);
    });
  }, [present]);

  // ✅ call this after successful save
  const markSaved = useCallback(() => {
    setSaved(structuredClone(present));
  }, [present]);

  const isDirty = useMemo(() => {
    if (present == null) return false;
    if (saved == null) return true;
    return !deepEqual(present, saved);
  }, [present, saved]);

  const value = useMemo(() => ({
    past, present, future,
    canUndo, canRedo,
    init, reset, commit, undo, redo,
    isDirty,
    markSaved,
  }), [past, present, future, canUndo, canRedo, init, reset, commit, undo, redo, isDirty, markSaved]);


  return <HistoryContext.Provider value={value}>{children}</HistoryContext.Provider>;
}

export function useHistory() {
  const ctx = useContext(HistoryContext);
  if (!ctx) throw new Error("useHistory must be used inside <HistoryProvider>");
  return ctx;
}

// For code that only READS the current config and may render where no
// provider is mounted (navigation helpers, layout tests): null instead of a throw.
export function useOptionalHistory() {
  return useContext(HistoryContext);
}
